import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Channel } from "../../../lib/api/channels";
import { useBoardStore } from "../../../stores/boardStore";
import { useAuthStore } from "../../../stores/authStore";

// Map entity type keywords to human-readable labels with translation support
const getEntityTypeLabel = (entityType: string, t: (key: string) => string): string => {
  const normalizedKey = entityType.replace(/_/g, '');
  const translationKey = `entityTypes.${normalizedKey}`;
  const translated = t(translationKey);

  // If i18n resolved (not returning the key itself), use it
  if (translated !== translationKey) return translated;

  // Fallback: Title Case from snake_case (e.g. "ad_account" → "Ad Account")
  return entityType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to calculate selection state for an element
const getElementSelectionState = (
  element: ChannelElement
): "checked" | "unchecked" | "partial" => {
  if (!element.children || element.children.length === 0) {
    return element.checked ? "checked" : "unchecked";
  }

  // Count checked children
  const totalChildren = getTotalChildrenCount(element);
  const checkedChildren = countCheckedChildren(element);

  if (checkedChildren === 0) {
    return "unchecked";
  } else if (checkedChildren === totalChildren) {
    return "checked";
  } else {
    return "partial";
  }
};

// Helper function to count total children recursively
const getTotalChildrenCount = (element: ChannelElement): number => {
  if (!element.children || element.children.length === 0) {
    return 1;
  }

  return element.children.reduce((count, child) => {
    return count + getTotalChildrenCount(child);
  }, 0);
};

// Helper function to count checked children recursively
const countCheckedChildren = (element: ChannelElement): number => {
  if (!element.children || element.children.length === 0) {
    return element.checked ? 1 : 0;
  }

  return element.children.reduce((count, child) => {
    return count + countCheckedChildren(child);
  }, 0);
};

interface ChannelElement {
  id: string;
  name: string;
  type: string;
  checked: boolean;
  parentId?: string;
  children?: ChannelElement[];
  currency?: string;
  feed?: {
    assets?: {
      type: 'image' | 'video' | 'mix' | 'none' | 'unknown';
      high: string[];
      low: string[];
    };
  };
  publishedAt?: string | Date | null;
}

interface ChannelsListProps {
  channels: Channel[];
  selectedEntityIds: string[];
  onToggleEntity: (entityIds: string | string[], isSelected: boolean) => void;
  onRemoveChannel: (channelId: string) => void;
  onCurrenciesChange?: (currencies: string[]) => void;
  rootEntityId?: string;
  onSelectionSummaryChange?: (count: number, total: number) => void;
  highlightedEntityId?: string;
  shouldHighlight?: boolean;
  onClearHighlight?: () => void;
}

export const ChannelsList: React.FC<ChannelsListProps> = ({
  channels,
  selectedEntityIds,
  onToggleEntity,
  onRemoveChannel: _onRemoveChannel,
  onCurrenciesChange,
  rootEntityId,
  onSelectionSummaryChange,
  highlightedEntityId,
  shouldHighlight,
  onClearHighlight,
}) => {
  const { t } = useTranslation("common");
  const { finance, unfilteredFinance, currencySettings } = useBoardStore();
  const { user } = useAuthStore();

  // Financial calculations
  const entityTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    const sourceData = unfilteredFinance || finance;
    if (!sourceData) return totals;

    // Preference 1: Detailed transactions for granular breakdown (even if rolled up on board)
    if (sourceData.transactions && Array.isArray(sourceData.transactions)) {
      sourceData.transactions.forEach((tx: any) => {
        const entityId = String(tx.originalEntityId || tx.entityId || '');
        if (!entityId || entityId === 'unallocated') return;

        const type = (tx.type || tx.transactionType || '').toUpperCase();
        const value = parseFloat(tx.amount || tx.value || 0);

        // In finance.transactions, amount is usually already converted if convertedAmount exists
        const convertedValue = tx.convertedAmount || value;

        if (totals[entityId] === undefined) totals[entityId] = 0;

        if (type === 'EXPENSE') totals[entityId] -= Math.abs(convertedValue);
        else if (type === 'REVENUE') totals[entityId] += convertedValue;
      });
      return totals;
    }

    // Preference 2: breakdown (fallback)
    if (sourceData.breakdown && Array.isArray(sourceData.breakdown)) {
      sourceData.breakdown.forEach((item: any) => {
        const entityId = String(item.entityId || item.entity_id || '');
        if (!entityId || entityId === 'unallocated') return;

        const type = (item.type || item.transactionType || '').toUpperCase();
        const value = parseFloat(item.value || item.amount || 0);

        if (totals[entityId] === undefined) totals[entityId] = 0;

        if (type === 'EXPENSE') totals[entityId] -= Math.abs(value);
        else if (type === 'REVENUE') totals[entityId] += value;
      });
    }

    return totals;
  }, [finance, unfilteredFinance]);

  // Currency formatting
  const currencyCode = currencySettings?.default || user?.currency || '';
  const getCurrencySymbol = (code: string) => {
    try {
      return (0).toLocaleString('en-US', { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0 }).replace(/[\d\s.,]/g, '');
    } catch {
      return '$';
    }
  }
  const symbol = getCurrencySymbol(currencyCode);

  const formatValue = (val: number) => {
    return val.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    });
  };

  // Recursive value aggregation for hierarchy
  const getAggregatedValue = React.useCallback((element: ChannelElement): number | null => {
    let total = entityTotals[element.id] || 0;
    let hasTransactions = entityTotals[element.id] !== undefined;

    if (element.children && element.children.length > 0) {
      element.children.forEach(child => {
        const childValue = getAggregatedValue(child);
        if (childValue !== null) {
          total += childValue;
          hasTransactions = true;
        }
      });
    }

    return hasTransactions ? total : null;
  }, [entityTotals]);

  // State for Tabs View
  const [activeTabLevel, setActiveTabLevel] = useState(0);
  const [focusPath, setFocusPath] = useState<Record<number, string>>({});
  const itemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  // Build hierarchical structure from flat entities
  const buildEntityHierarchy = (entities: any[]): ChannelElement[] => {
    // Ensure entities is an array, if not return empty array
    if (!Array.isArray(entities)) {
      console.warn("buildEntityHierarchy: entities is not an array", entities);
      return [];
    }

    const entityMap = new Map();
    const rootElements: ChannelElement[] = [];

    // First pass: create all elements and map by id
    entities.forEach((entity) => {
      const isSelected = selectedEntityIds.includes(entity.id);
      const element: ChannelElement = {
        id: entity.id,
        name: entity.name || t("common.unnamed") || "Unnamed Entity",
        type: entity.entityType || "unknown",
        parentId: entity.parentId,
        checked: isSelected,
        children: [],
        currency: entity.currency,
        feed: {
          assets: entity.assets || (entity.feed?.assets) || []
        },
        publishedAt: entity.publishedAt
      };
      entityMap.set(entity.id, element);
    });

    // Second pass: build hierarchy - only add children to existing parents
    entities.forEach((entity) => {
      const element = entityMap.get(entity.id);

      if (entity.parentId && entityMap.has(entity.parentId)) {
        const parent = entityMap.get(entity.parentId);
        if (parent.children) {
          parent.children.push(element);
        } else {
          parent.children = [element];
        }
      } else {
        // Only add to root if it doesn't have a parent OR if the parent doesn't exist in our map
        rootElements.push(element);
      }
    });

    // Find all descendants recursively if we need to search for root
    const findEntity = (elements: ChannelElement[], id: string): ChannelElement | null => {
      for (const el of elements) {
        if (el.id === id) return el;
        if (el.children) {
          const found = findEntity(el.children, id);
          if (found) return found;
        }
      }
      return null;
    };

    if (rootEntityId) {
      const searchInRoots = (elements: ChannelElement[]) => {
        const found = findEntity(elements, rootEntityId);
        if (found && found.children) {
          return found.children;
        }
        return null;
      };

      const result = searchInRoots(rootElements);
      if (result) return result;

      console.warn(`rootEntityId ${rootEntityId} not found in hierarchy or has no children.`);
      return [];
    }

    return rootElements;
  };

  // Build channels with hierarchical elements from store data
  const channelsWithElements = useMemo(() => {
    if (!channels.length) return [];

    // Build channel items with hierarchical elements
    return channels.map((channel: any) => {
      // Check if channel is loading entities or is in syncing state
      const isLoading =
        channel.isLoadingEntities === true ||
        (channel.status && channel.status.toLowerCase() === 'syncing');

      // Use entities from the channel object
      let channelEntities = channel.entities || [];

      // If channelEntities is an object with an entities array, extract the array
      if (
        channelEntities &&
        typeof channelEntities === "object" &&
        !Array.isArray(channelEntities) &&
        Array.isArray(channelEntities.entities)
      ) {
        channelEntities = channelEntities.entities;
      }

      // Ensure channelEntities is an array
      if (!Array.isArray(channelEntities)) {
        channelEntities = [];
      }

      const hierarchicalElements = isLoading
        ? []
        : buildEntityHierarchy(channelEntities);

      // Check if any element in the channel is selected
      const checked = channelEntities.some((entity: any) =>
        selectedEntityIds.includes(entity.id)
      );

      return {
        id: channel.id,
        name: channel.name,
        channelType: channel.channelType,
        status: channel.status,
        checked: checked,
        elements: hierarchicalElements,
        isLoadingEntities: isLoading,
        icon: channel.icon,
        channelTypeConfig: channel.channelTypeConfig,
      };
    });
  }, [channels, selectedEntityIds, t, rootEntityId]);

  // Auto-navigate to highlighted entity
  useEffect(() => {
    if (!highlightedEntityId || channelsWithElements.length === 0) return;

    // We assume single channel context for highlighted entities in this modal
    const channel = channelsWithElements[0];
    if (!channel || !channel.elements) return;

    // Find all flattened elements to search for path
    const allElements: ChannelElement[] = [];
    const flatten = (items: ChannelElement[]) => {
      items.forEach(item => {
        allElements.push(item);
        if (item.children) flatten(item.children);
      });
    };
    flatten(channel.elements);

    const target = allElements.find(e => String(e.id) === String(highlightedEntityId));
    if (!target) return;

    // Build path from target to root
    const path: Record<number, string> = {};
    let current: ChannelElement | undefined = target;
    const pathNodes: ChannelElement[] = [];

    while (current) {
      pathNodes.unshift(current);
      if (!current.parentId) break;
      current = allElements.find(e => String(e.id) === String(current?.parentId));
    }

    // Set focus path for tabs
    pathNodes.forEach((node, index) => {
      path[index] = node.id;
    });

    setFocusPath(path);
    setActiveTabLevel(Math.max(0, pathNodes.length - 1));
  }, [highlightedEntityId, channelsWithElements]);

  const hasScrolledRef = React.useRef<string | null>(null);

  // Scroll and highlight animation
  useEffect(() => {
    if (shouldHighlight && highlightedEntityId && itemRefs.current[highlightedEntityId]) {
      // Check if we already scrolled for this specific ID in this session
      if (hasScrolledRef.current === highlightedEntityId) return;

      const element = itemRefs.current[highlightedEntityId];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Mark as scrolled
        hasScrolledRef.current = highlightedEntityId;

        element.classList.add('entity-highlight-pulse');

        // Clear highlight flag in parent state
        if (onClearHighlight) onClearHighlight();

        const timer = setTimeout(() => {
          if (element) element.classList.remove('entity-highlight-pulse');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [shouldHighlight, highlightedEntityId, activeTabLevel, focusPath, onClearHighlight]);

  // Reset scroll lock when ID changes or shouldHighlight is reset
  useEffect(() => {
    if (!shouldHighlight) {
      hasScrolledRef.current = null;
    }
  }, [shouldHighlight, highlightedEntityId]);

  // Compute Hierarchy Levels for Tabs View
  const hierarchyLevels = useMemo(() => {
    if (channelsWithElements.length === 0 || channelsWithElements[0].elements.length === 0) return [];

    const levels: { level: number; type: string }[] = [];
    let currentLevel = 0;
    let currentNodes = channelsWithElements[0].elements;

    while (currentNodes.length > 0) {
      // Determine most common type for this level
      const typeCounts: Record<string, number> = {};
      currentNodes.forEach(node => {
        const type = node.type || "Unknown";
        typeCounts[type] = (typeCounts[type] || 0) + 1;
      });

      const bestType = Object.entries(typeCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || `Level ${currentLevel + 1}`;

      levels.push({
        level: currentLevel,
        type: bestType.charAt(0).toUpperCase() + bestType.slice(1) // Capitalize
      });

      // Gather next level nodes
      const nextNodes: ChannelElement[] = [];
      currentNodes.forEach(node => {
        if (node.children) {
          nextNodes.push(...node.children);
        }
      });

      currentNodes = nextNodes;
      currentLevel++;
    }

    return levels;
  }, [channelsWithElements]);

  // Get filtered items for the current active tab
  const getCurrentTabItems = () => {
    if (channelsWithElements.length === 0) return [];
    const rootElements = channelsWithElements[0].elements;

    if (hierarchyLevels.length === 0) return [];

    let currentItems = rootElements;

    // Traverse down complexity
    for (let i = 0; i < activeTabLevel; i++) {
      // Identify "active" parents at this level (i)
      // An item is active if it is Checked (selectedEntityIds) OR Focused (focusPath)
      const activeParents = currentItems.filter(item =>
        getElementSelectionState(item) !== 'unchecked' || focusPath[i] === item.id
      );

      if (activeParents.length > 0) {
        // If there are specific active parents, show only their children
        currentItems = activeParents.flatMap(parent => parent.children || []);
      } else {
        // If no explicit filtering, show children of ALL items at this level
        currentItems = currentItems.flatMap(item => item.children || []);
      }
    }
    return currentItems;
  };

  const currentTabItems = useMemo(() => {
    const items = getCurrentTabItems();
    return [...items].sort((a, b) => {
      // Primary sort by publishedAt (newest first)
      if (a.publishedAt || b.publishedAt) {
        const dateA = a.publishedAt ? new Date(a.publishedAt).getTime() : 0;
        const dateB = b.publishedAt ? new Date(b.publishedAt).getTime() : 0;
        if (dateA !== dateB) return dateB - dateA; // Descending
      }

      // Secondary sort by aggregated value (highest first)
      const valA = Math.abs(getAggregatedValue(a) || 0);
      const valB = Math.abs(getAggregatedValue(b) || 0);
      return valB - valA;
    });
  }, [activeTabLevel, focusPath, channelsWithElements, entityTotals]); // Added necessary deps for sorting

  const handleTabItemClick = (item: ChannelElement) => {
    // Focus this item for the current level
    setFocusPath(prev => {
      const newPath = { ...prev };
      // Set focus for this level
      newPath[activeTabLevel] = item.id;
      // Clear focus for deeper levels as context changed
      for (let i = activeTabLevel + 1; i < hierarchyLevels.length; i++) {
        delete newPath[i];
      }
      return newPath;
    });

    // If there are deeper levels, auto-switch to next tab
    if (activeTabLevel < hierarchyLevels.length - 1) {
      setActiveTabLevel(activeTabLevel + 1);
    }
  };

  // Collect all unique currencies from entities and notify parent
  useEffect(() => {
    if (!onCurrenciesChange) return;

    const currencies = new Set<string>();

    const collectCurrencies = (elements: ChannelElement[]) => {
      elements.forEach((element) => {
        if (element.currency) {
          currencies.add(element.currency);
        }
        if (element.children) {
          collectCurrencies(element.children);
        }
      });
    };

    channelsWithElements.forEach((channel) => {
      collectCurrencies(channel.elements);
    });

    onCurrenciesChange(Array.from(currencies));
  }, [channelsWithElements, onCurrenciesChange]);

  // Report selection summary to parent
  useEffect(() => {
    if (!onSelectionSummaryChange) return;

    let totalSelectedValue = 0;
    let totalSelectedCount = 0;

    // Get all entity IDs that belong to the current channels
    const channelEntityIds = new Set<string>();
    const collectIds = (elements: ChannelElement[]) => {
      elements.forEach(el => {
        channelEntityIds.add(el.id);
        if (el.children) collectIds(el.children);
      });
    };
    channelsWithElements.forEach(ch => collectIds(ch.elements));

    // Calculate sum of totals for selected entities in these channels
    selectedEntityIds.forEach(id => {
      if (channelEntityIds.has(id)) {
        totalSelectedCount++;
        if (entityTotals[id] !== undefined) {
          totalSelectedValue += entityTotals[id];
        }
      }
    });

    onSelectionSummaryChange(totalSelectedCount, totalSelectedValue);
  }, [selectedEntityIds, entityTotals, channelsWithElements, onSelectionSummaryChange]);

  const handleElementCheck = (
    _channelId: string,
    elementId: string,
    checked: boolean
  ) => {
    // Find the entity in the hierarchical structure to get its children
    let targetEntity: ChannelElement | null = null;
    let targetAncestors: string[] = [];

    // Helper to search recursively through hierarchical elements
    const findEntityInHierarchy = (
      elements: ChannelElement[],
      targetId: string,
      currentPath: string[] = []
    ): ChannelElement | null => {
      for (const element of elements) {
        if (element.id === targetId) {
          targetAncestors = [...currentPath];
          return element;
        }
        if (element.children) {
          const found = findEntityInHierarchy(element.children, targetId, [...currentPath, element.id]);
          if (found) return found;
        }
      }
      return null;
    };

    // Search in all channels
    for (const channel of channelsWithElements) {
      targetEntity = findEntityInHierarchy(channel.elements, elementId);
      if (targetEntity) break;
    }

    // Find all descendants of this element recursively
    const getAllDescendantIds = (entity: ChannelElement): string[] => {
      const ids: string[] = [];
      if (entity.children) {
        entity.children.forEach((child) => {
          ids.push(child.id);
          ids.push(...getAllDescendantIds(child));
        });
      }
      return ids;
    };

    const descendantIds = targetEntity ? getAllDescendantIds(targetEntity) : [];

    // If we are unchecking, we MUST also uncheck all ancestors to break the "select all" inheritance
    const idsToToggle = [elementId, ...descendantIds];
    if (!checked && targetAncestors.length > 0) {
      idsToToggle.push(...targetAncestors);
    }

    // Include the element itself, its descendants, and (if unchecking) its ancestors
    onToggleEntity(idsToToggle, checked);
  };

  // Helper to get tab selection state
  const getTabSelectionState = (items: ChannelElement[]): "checked" | "unchecked" | "partial" => {
    if (items.length === 0) return "unchecked";

    let totalCount = 0;
    let checkedCount = 0;

    items.forEach(item => {
      const itemTotal = getTotalChildrenCount(item);
      const itemChecked = countCheckedChildren(item);
      totalCount += itemTotal;
      checkedCount += itemChecked;
    });

    if (checkedCount === 0) return "unchecked";
    if (checkedCount === totalCount) return "checked";
    return "partial";
  };

  // Helper to handle tab select all
  const handleTabSelectAll = (items: ChannelElement[], checked: boolean) => {
    if (channelsWithElements.length === 0) return;

    const collectAllIds = (elements: ChannelElement[]): string[] => {
      const ids: string[] = [];
      const collect = (els: ChannelElement[]) => {
        els.forEach((el) => {
          ids.push(el.id);
          if (el.children) {
            collect(el.children);
          }
        });
      };
      collect(elements);
      return ids;
    };

    const allIds = collectAllIds(items);

    // Also uncheck ancestors of these items if unchecking
    if (!checked) {
      // Find ancestors of the first item (all items in a tab usually share the same focused ancestors)
      const targetAncestors = new Set<string>();

      const findAncestors = (
        elements: ChannelElement[],
        targetIds: Set<string>,
        currentPath: string[] = []
      ) => {
        for (const element of elements) {
          if (targetIds.has(element.id)) {
            currentPath.forEach(p => targetAncestors.add(p));
          }
          if (element.children) {
            findAncestors(element.children, targetIds, [...currentPath, element.id]);
          }
        }
      };

      const itemsIdSet = new Set(items.map(i => i.id));
      for (const channel of channelsWithElements) {
        findAncestors(channel.elements, itemsIdSet);
      }

      allIds.push(...Array.from(targetAncestors));
    }

    onToggleEntity(allIds, checked);
  };

  // Show empty state if no hierarchy levels
  if (hierarchyLevels.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-neutral-400 text-sm">
        <p>{t('common.noItemsFound')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white dark:bg-neutral-950 rounded-xl overflow-hidden">
      {/* Tabs Header */}
      <div className="flex items-center gap-1 p-2 bg-neutral-50/50 dark:bg-neutral-900/50 border-b border-neutral-100 dark:border-neutral-800 overflow-x-auto scrollbar-hide">
        {hierarchyLevels.map((levelInfo, idx) => {
          const isActive = idx === activeTabLevel;

          // Get items for this specific tab level
          let tabItems: ChannelElement[] = [];
          if (channelsWithElements.length > 0) {
            let currentItems = channelsWithElements[0].elements;
            for (let i = 0; i < idx; i++) {
              const activeParents = currentItems.filter(item =>
                getElementSelectionState(item) !== 'unchecked' || focusPath[i] === item.id
              );
              if (activeParents.length > 0) {
                currentItems = activeParents.flatMap(parent => parent.children || []);
              } else {
                currentItems = currentItems.flatMap(item => item.children || []);
              }
            }
            tabItems = currentItems;
          }
          const tabSelectionState = getTabSelectionState(tabItems);

          return (
            <button
              key={idx}
              onClick={() => setActiveTabLevel(idx)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap
                ${isActive
                  ? 'bg-white dark:bg-neutral-800 text-neutral-800 dark:text-neutral-100 shadow-sm border border-neutral-200 dark:border-neutral-700'
                  : 'text-neutral-500 dark:text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                }
              `}
            >
              {/* Tab Selection Checkbox */}
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  const newChecked = tabSelectionState === "checked" ? false : true;
                  handleTabSelectAll(tabItems, newChecked);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    const newChecked = tabSelectionState === "checked" ? false : true;
                    handleTabSelectAll(tabItems, newChecked);
                  }
                }}
                className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 transition-colors cursor-pointer"
                title={
                  tabSelectionState === "checked"
                    ? (t("channels.active") as string)
                    : tabSelectionState === "partial"
                      ? (t("channels.partial") as string)
                      : (t("channels.inactive") as string)
                }
              >
                {tabSelectionState !== 'unchecked' && (
                  <div className={`w-2.5 h-2.5 rounded-sm ${tabSelectionState === 'partial' ? 'bg-neutral-400 dark:bg-neutral-500' : 'bg-accent-primary'}`} />
                )}
              </div>
              {getEntityTypeLabel(levelInfo.type, t)}
            </button>
          );
        })}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto min-h-0 p-2">
        {currentTabItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 text-neutral-400 dark:text-neutral-500 text-sm">
            <p>{t('common.noItemsFound')}</p>
          </div>
        ) : (
          <div className={
            (function () {
              const getEntityImage = (item: ChannelElement) => {
                if (!item.feed?.assets) return null;
                if (Array.isArray(item.feed.assets.high) && item.feed.assets.high.length > 0) return item.feed.assets.high[0];
                if (Array.isArray(item.feed.assets.low) && item.feed.assets.low.length > 0) return item.feed.assets.low[0];
                return null;
              };
              const hasImgs = currentTabItems.some((e) => getEntityImage(e) !== null);
              return hasImgs ? "grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" : "space-y-1";
            })()
          }>
            {currentTabItems.map(item => {
              const selectionState = getElementSelectionState(item);

              const getEntityImage = (item: ChannelElement) => {
                if (!item.feed?.assets) return null;
                if (Array.isArray(item.feed.assets.high) && item.feed.assets.high.length > 0) return item.feed.assets.high[0];
                if (Array.isArray(item.feed.assets.low) && item.feed.assets.low.length > 0) return item.feed.assets.low[0];
                return null;
              };
              const imageUrl = getEntityImage(item);

              const hasImgs = currentTabItems.some((e) => getEntityImage(e) !== null);
              const layoutMode = hasImgs ? 'grid' : 'list';

              const val = getAggregatedValue(item);
              const isNegative = val !== null && val < 0;

              if (layoutMode === 'grid') {
                return (
                  <div
                    key={item.id}
                    ref={el => { itemRefs.current[item.id] = el; }}
                    onClick={() => {
                      if (onClearHighlight) onClearHighlight();
                      handleTabItemClick(item);
                    }}
                    className={`relative w-full rounded-2xl overflow-hidden text-left group transition-all duration-300 cursor-pointer ${item.id === highlightedEntityId ? 'ring-2 ring-accent-primary ring-offset-2' : 'hover:ring-2 hover:ring-white/40 ring-offset-2 dark:ring-offset-neutral-950 shadow-md hover:shadow-xl'}`}
                    title={item.name}
                  >
                    {/* Image Background */}
                    <div className="absolute inset-0 bg-neutral-100 dark:bg-neutral-800">
                      {imageUrl ? (
                        <>
                          <img src={imageUrl} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                          {(item.feed?.assets?.type === 'video' || item.feed?.assets?.type === 'mix') && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-md flex items-center justify-center shadow-lg border border-white/20 group-hover:scale-110 group-hover:bg-black/40 transition-all duration-300 ease-out">
                                <svg className="w-6 h-6 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M8 5v14l11-7z" />
                                </svg>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center scale-150 opacity-10">
                          {getEntityTypeLabel(item.type, t)}
                        </div>
                      )}
                    </div>

                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t transition-opacity duration-300 from-black/90 via-black/40 to-black/10 group-hover:via-black/50"></div>

                    {/* Content Overlay */}
                    <div className="relative h-48 sm:h-56 p-4 flex flex-col justify-between">
                      {/* Top section: Checkbox */}
                      <div className="flex justify-end">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const newChecked = selectionState === "checked" ? false : true;
                            if (channelsWithElements.length > 0) {
                              handleElementCheck(channelsWithElements[0].id, item.id, newChecked);
                            }
                          }}
                          className={`flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-md border ${selectionState !== 'unchecked' ? 'bg-accent-primary border-accent-primary text-white shadow-lg' : 'bg-black/30 border-white/20 text-transparent hover:bg-black/50 backdrop-blur-sm'} transition-all`}
                        >
                          <span className={`w-3.5 h-3.5 ${selectionState === 'partial' ? 'bg-white rounded-sm' : ''}`} style={selectionState === 'checked' ? { maskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_02c569e3.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' } : {}} role={selectionState === 'checked' ? 'img' : undefined} />
                        </button>
                      </div>

                      {/* Bottom section: Text content */}
                      <div>
                        <div className="flex justify-between items-end gap-2 mb-1">
                          <div className="min-w-0 flex-1">
                            <h3 className="text-white font-bold text-sm tracking-tight leading-tight truncate drop-shadow-md">
                              {item.name}
                            </h3>
                          </div>
                          {val !== null && (
                            <div className={`text-xs font-bold whitespace-nowrap bg-black/60 backdrop-blur-md px-2 py-1 rounded-lg shadow-sm border border-white/10 ${isNegative ? 'text-rose-400' : 'text-emerald-400'}`}>
                              {symbol}{formatValue(Math.abs(val))}
                            </div>
                          )}
                        </div>

                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div
                  key={item.id}
                  ref={el => { itemRefs.current[item.id] = el; }}
                  onClick={() => {
                    if (onClearHighlight) onClearHighlight();
                    handleTabItemClick(item);
                  }}
                  className={`group flex items-center gap-3 p-3 rounded-lg cursor-pointer border transition-all ${item.id === highlightedEntityId ? 'border-accent-primary ring-2 ring-accent-primary/20 bg-accent-primary/5' : 'bg-white dark:bg-neutral-900 border-neutral-100 dark:border-neutral-800 hover:border-neutral-200 dark:hover:border-neutral-700 hover:shadow-sm'}`}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newChecked = selectionState === "checked" ? false : true;
                      // Note: using channelsWithElements[0].id assumes single channel in tabs mode
                      if (channelsWithElements.length > 0) {
                        handleElementCheck(channelsWithElements[0].id, item.id, newChecked);
                      }
                    }}
                    className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded border border-neutral-200 dark:border-neutral-700 group-hover:border-neutral-300 dark:group-hover:border-neutral-600 bg-white dark:bg-neutral-800 transition-colors"
                  >
                    {selectionState !== 'unchecked' && (
                      <div className={`w-3 h-3 rounded-sm ${selectionState === 'partial' ? 'bg-neutral-400 dark:bg-neutral-500' : 'bg-accent-primary'}`} />
                    )}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                      {item.name}
                    </div>
                    <div className="text-xs text-neutral-400 dark:text-neutral-500">
                      <span>{getEntityTypeLabel(item.type, t)}</span>
                    </div>
                  </div>

                  {(() => {
                    if (val === null) return null;
                    return (
                      <div className={`text-xs font-bold flex-shrink-0 ${isNegative ? 'text-rose-600' : 'text-emerald-600'}`}>
                        {symbol}{formatValue(Math.abs(val))}
                      </div>
                    );
                  })()}

                  {(item.children && item.children.length > 0) && (
                    <div className="transition-colors text-neutral-300 dark:text-neutral-600 group-hover:text-neutral-500 dark:group-hover:text-neutral-400">
                      <span className="w-4 h-4" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_640733f4.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_640733f4.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
